/* eslint-disable react-hooks/rules-of-hooks */

import {
  useMemo, useState, useEffect, useRef, useCallback,
} from 'react';
import {
  GoogleMap, useJsApiLoader, MarkerF, PolygonF, CircleF,
} from '@react-google-maps/api';
import Supercluster, { ClusterFeature, PointFeature } from 'supercluster';
import { useTheme } from 'styled-components';
import VehicleRoute from './VehicleRoute/VehicleRoute';
import VehicleMarker from './VehicleMarker/VehicleMarker';
import { darkStyles, lightStyles } from './styles';
import {
  getDefaultCenterAndZoom,
  isCirclePolygon,
  getCircleParams,
  convertToGooglePath,
} from './helpers';
import { LocationDetails, LocationsItem } from '../../../API/types/Maps';
import {
  LocationsArray, Locations, Map,
} from './types';
import ClusterPng from './cluster.png';
import AlertMarker from './AlertMarker/AlertMarker';
import useGetRole from '../../../API/hooks/useGetRole';
import { AlertDetails, AlertsHeatmapResponse } from '../../../API/types/Alerts';
import { useThrottle } from '../../../utils/hooks/useThrottle';
import { ThemeType } from '../../../theme/themeType';
import { useAppTheme } from '../../../theme/ThemeContext';
import S from './MapStyles';
import { logger } from '../../../API/services/SharedService';
import { FeaturesManagement } from '../../../featuresControl';
import { useSnapToRoad } from './hooks';
import { useAlertsHeatmapDeckGl } from './useAlertsHeatmapDeckGl';
import useGetGeofencesList from '../../../API/hooks/geofences/useGetGeofencesList';
import { getLocalStorageKey } from '../../../utils/storageUtils';
import { Geofence } from '../../../API/types/Geofences';
import MapWidgetSkeleton from './MapWidgetSkeleton';
import { GEOFENCE_STYLE, INITIAL_CENTER, DETAILS_LIMIT } from './constants';
import { queryFilterByGroups, getSimpleEqQuery } from '../../../utils/queryFilters';
import useLocalStorage from '../../../API/hooks/useLocalStorage';
import PointMarker from './PointMarker/PointMarker';
import { buildSpeedMsByEntityId } from './locationSpeed';
import useMarkerColors from './useMarkerColors';
import { DEFAULT_MARKER_COLOR } from './markerColors';
import RouteHistory from './RouteHistory/RouteHistory';
import {
  buildRouteHistoryPath,
  getRouteHistoryRange,
  getRouteHistoryTitle,
  EMPTY_ROUTE_HISTORY_PATH,
  ROUTE_HISTORY_POINTS_LIMIT,
  ROUTE_HISTORY_TRIPS_LOOKBEHIND_MS,
} from './routeHistory';
import useGetTripsRange from '../../../API/hooks/trips/useGetTripsRange';
import { useAlertFocus } from '../../../API/hooks/useAlertFocus';
import alertFocusStore, { FocusedAlert } from '../../../store/AlertFocusStore';
import type {
  OngoingTripSpeedDisplayByVehicleId,
  OngoingTripSpeedDisplayByDeviceId,
  OngoingTripSpeedDisplayMap,
} from '../../../API/hooks/trips/useOngoingTripMapIds';

type ButtonProps = {
  title?: string;
  className?: string;
  onClick?: (...args: any) => void;
};

const createCustomButton = ({
  onClick, className = '', title = '',
}: ButtonProps): HTMLElement => {
  const customButton = document.createElement('button');
  customButton.type = 'button';
  customButton.title = title;
  customButton.classList.add('custom-map-button', className);
  customButton.addEventListener('click', () => {
    if (onClick) {
      onClick();
    }
  });

  return customButton;
};

const baseOptions = {
  streetViewControl: false,
  mapTypeControl: true,
  mapTypeControlOptions: {
    mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
  },
  fullscreenControl: false,
  minZoom: 2,
  maxZoom: 19,
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      west: -180,
      east: 180,
    },
    strictBounds: false,
  },
  scaleControl: true,
  keyboardShortcuts: false,
  gestureHandling: 'greedy',
};
// `styles` (Google Maps' own dark/light color scheme) has to react to the app theme
// toggle, so it can't live on `baseOptions` - it's merged in per-render inside MapWidget.
const options = { ...baseOptions, styles: darkStyles };
const libs = ['places', 'geometry'];
// TODO: enable clustering
const sc = new Supercluster({ radius: 0, maxZoom: 0 }); // radius: 40, maxZoom: options.maxZoom

type FiltersType = {
  pointsLimit: number;
  entityId?: string;
  polygons?: number[][];
  startDate?: string;
  endDate?: string;
  /** Points limit used when a date range is requested; defaults to the trip-sized limit. */
  rangePointsLimit?: number;
  groupFilters?: Record<string, string[]>;
  tripId?: string;
};

function formatDataToGeoJsonPoints(
  locations: LocationsArray,
  ids: (string | undefined)[],
  speedById: Record<string, number>,
  tripSpeedMs?: number,
  detailOverlaySpeedMs?: number,
  /** Ongoing trip map: merged trip speed only; do not fill from device location speed. */
  skipSpeedByIdFallback?: boolean,
): GeoJSON.Feature<GeoJSON.Point>[] {
  return locations?.map((location, idx) => {
    const id = ids[idx];
    const fromDevice = id != null && id !== '' && speedById[id] !== undefined
      ? speedById[id]
      : undefined;
    const speedMs = tripSpeedMs ?? detailOverlaySpeedMs
      ?? (skipSpeedByIdFallback ? undefined : fromDevice);
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [location?.[0]?.[1], location?.[0]?.[0]],
      },
      properties: {
        cluster: false, location, id, speedMs,
      },
    };
  });
}

function getLabel(pointCount: number, theme: ThemeType): google.maps.MarkerLabel {
  return { text: pointCount.toString(), color: theme.colors.black, fontWeight: 'bold' };
}

function getPointLocations(points: LocationsItem['coordinates']): Locations {
  return points
    ?.filter((point) => point?.latitude && point?.longitude)
    ?.map((point) => ([
      point.latitude,
      point.longitude,
    ]));
}

function getPointLocationsDetails(points: LocationDetails[]): Locations {
  return points
    ?.filter((point) => point?.location?.coordinates?.[0] && point?.location?.coordinates?.[1])
    ?.map((point: LocationDetails) => ([
      point.location.coordinates[1],
      point.location.coordinates[0],
    ]));
}

function parseLocations(data: any, details?: boolean): LocationsArray {
  let locations;
  if (details) {
    locations = [getPointLocationsDetails(data)];
  } else {
    locations = data?.map((item: LocationsItem) => (
      getPointLocations(item.coordinates)
    )).filter((item: LocationsArray) => item?.length);
  }

  return data?.length ? locations : [];
}

const getQueryParams = (filters: FiltersType): string => {
  let query = filters?.entityId ? `?pointsLimit=${filters?.pointsLimit || 2}` : '';
  if (filters?.startDate && filters?.endDate) {
    const rangePointsLimit = filters.rangePointsLimit ?? 100000;
    query = `?from=${filters.startDate}&to=${filters.endDate}&pointsLimit=${rangePointsLimit}`;
  }
  if (filters?.polygons?.length) {
    query += filters.polygons.reduce(
      (result, polygon, index) => `${result}&polygons[${index}][0]=${polygon[0]}&polygons[${index}][1]=${polygon[1]}`,
      '',
    );
  }
  if (filters?.groupFilters?.groupIds) {
    query += queryFilterByGroups(filters.groupFilters);
  }
  if (filters.tripId) {
    const tripParam = `tripId=${encodeURIComponent(filters.tripId)}`;
    query = query ? `${query}&${tripParam}` : `?${tripParam}`;
  }
  return query.startsWith('?') ? query : query.replace('&', '?');
};

const createKey = (location: Locations, index: number): number => (
  index + location[0][0] + location[0][1]
);

const getAlertPositions = (alerts?: AlertDetails[]): any => (
  (alerts || [])
    ?.filter((alert) => alert?.lat && alert?.lon)
    ?.map((alert) => [[alert.lat, alert.lon]])
);

const getHeatmapPositions = (heatMap?: AlertsHeatmapResponse): any => (
  heatMap
    ?.filter((item) => item?.lat && item?.lon)
    ?.map((item) => [[item.lat, item.lon]])
);

function fitMapToLocations(map: google.maps.Map, locations: LocationsArray, customZoom?: number, async = false): void {
  const bounds = new google.maps.LatLngBounds();
  locations.forEach((location) => {
    location.forEach((point) => {
      bounds.extend(new google.maps.LatLng(point[0], point[1]));
    });
  });
  map.fitBounds(bounds);
  if (customZoom) {
    if (async) {
      // we need to apply custom zoom after the async fitBounds
      google.maps.event.addListenerOnce(map, 'idle', () => {
        map.setZoom(customZoom);
      });
    } else {
      map.setZoom(customZoom);
    }
  }
}

type Props = {
  fleetId: string;
  entityId?: string;
  entityType?: string;
  getLocationsQuery?: Function;
  getLocationDetailsQuery?: Function;
  alerts?: AlertDetails[];
  heatMap?: AlertsHeatmapResponse;
  startDate?: string;
  endDate?: string;
  groupFilters?: Record<string, string[]>;
  tripId?: string;
  tripVehicleId?: string;
  tripSpeedMs?: number | null;
  isTrip?: boolean;
  isOngoingTrip?: boolean;
  hasActiveTripForSpeed?: boolean;
  fleetOngoingVehicleIds?: string[];
  fleetOngoingDeviceIds?: string[];
  /** Fleet maps (vehicle/trip/video): merged active-trip speed by vehicleId; null = no GPS fallback. */
  ongoingTripSpeedDisplayByVehicleId?: OngoingTripSpeedDisplayByVehicleId;
  /** Fleet device map: merged active-trip speed by deviceId; null = no GPS fallback. */
  ongoingTripSpeedDisplayByDeviceId?: OngoingTripSpeedDisplayByDeviceId;
  usePolygons?: boolean;
  /**
   * Page date filters (startDate/endDate). Passing them on an entity detail map enables the
   * route-history overlay: the path the entity travelled during the selected period.
   */
  routeHistoryFilters?: Record<string, string[]>;
  hasTripVehicleMarker?: boolean;
  alertsLoaded?: boolean;
  showSpeedInVehicleInfo?: boolean;
  showDetailsButton?: boolean;
  mapWrapperWidth?: string;
  disableAlertMarkerNavigation?: boolean;
};

function MapWidget({
  fleetId,
  entityId,
  entityType,
  getLocationsQuery,
  getLocationDetailsQuery,
  startDate,
  endDate,
  groupFilters,
  tripId,
  tripVehicleId,
  tripSpeedMs,
  alerts = [],
  heatMap = [],
  isTrip = false,
  isOngoingTrip = false,
  hasActiveTripForSpeed,
  fleetOngoingVehicleIds,
  fleetOngoingDeviceIds,
  ongoingTripSpeedDisplayByVehicleId,
  ongoingTripSpeedDisplayByDeviceId,
  usePolygons = false,
  routeHistoryFilters,
  hasTripVehicleMarker = false,
  alertsLoaded = false,
  showSpeedInVehicleInfo = false,
  showDetailsButton = true,
  mapWrapperWidth,
  disableAlertMarkerNavigation = false,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const { themeMode } = useAppTheme();
  const mapOptions = useMemo(() => ({
    ...baseOptions,
    styles: themeMode === 'light' ? lightStyles : darkStyles,
  }), [themeMode]);
  const wrapperStyle = mapWrapperWidth ? {
    width: mapWrapperWidth,
    minWidth: 0,
    transition: 'width 0.3s ease',
    flexShrink: 0,
  } : undefined;
  const containerStyle = mapWrapperWidth ? { width: '100%' } : undefined;

  if (!FeaturesManagement.productivity.map) {
    return (
      <S.Wrapper className="map-wrapper" style={wrapperStyle}>
        <MapWidgetSkeleton />
      </S.Wrapper>
    );
  }

  const [filters, setFilters] = useState<FiltersType>({
    entityId,
    pointsLimit: 2,
  });
  const [showGeofencesStorage, setShowGeofencesStorage] = useLocalStorage(getLocalStorageKey('show-geofences'), true);
  const [showGeofences, setShowGeofences] = useState<boolean>(FeaturesManagement.features.geofence && (showGeofencesStorage !== undefined ? showGeofencesStorage : true));
  const queryParams = getQueryParams({
    ...filters, startDate, endDate, entityId, groupFilters, tripId,
  });
  const throttledQueryParams = useThrottle(queryParams);
  const { data, isSuccess } = !getLocationsQuery ? { data: null, isSuccess: false }
    : getLocationsQuery(fleetId, throttledQueryParams, entityId, !(isTrip && !isOngoingTrip));

  const { isFleetUser, isAdmin } = useGetRole();

  // Route history: a second, non-polling read of the same endpoint over the selected period.
  // Kept separate from the live query above so the marker keeps updating every few seconds
  // without re-downloading weeks of GPS points each time. System admins only - it is a heavy
  // read of raw GPS history.
  const routeHistorySupported = FeaturesManagement.map.routeHistory && isAdmin
    && !!getLocationsQuery && !!entityId && !isTrip && !!routeHistoryFilters;
  const [showRouteHistoryStorage, setShowRouteHistoryStorage] = useLocalStorage(
    getLocalStorageKey('show-route-history'),
    false,
  );
  const [showRouteHistory, setShowRouteHistory] = useState<boolean>(!!showRouteHistoryStorage);
  const routeHistoryStartDate = routeHistoryFilters?.startDate?.[0];
  const routeHistoryEndDate = routeHistoryFilters?.endDate?.[0];
  const routeHistoryRange = useMemo(
    () => (routeHistorySupported ? getRouteHistoryRange(routeHistoryStartDate, routeHistoryEndDate) : null),
    [routeHistorySupported, routeHistoryStartDate, routeHistoryEndDate],
  );
  const routeHistoryEnabled = !!routeHistoryRange && showRouteHistory;
  const routeHistoryQueryParams = routeHistoryRange ? getQueryParams({
    pointsLimit: 2,
    entityId,
    startDate: routeHistoryRange.from,
    endDate: routeHistoryRange.to,
    rangePointsLimit: ROUTE_HISTORY_POINTS_LIMIT,
  }) : '';
  const { data: routeHistoryData, isFetching: routeHistoryFetching } = !getLocationsQuery
    ? { data: null, isFetching: false }
    : getLocationsQuery(fleetId, routeHistoryQueryParams, routeHistoryEnabled ? entityId : '', false);

  // Trips of the same window, used to cut the path into legs that open their trip details.
  // The lookbehind keeps points of a trip that started before the window attributed to it.
  const routeHistoryTripsQuery = routeHistoryRange ? [
    '?limit=500&skip=0&sortField=startAt&sortOrder=ASC',
    getSimpleEqQuery(`${entityType}Id`, entityId, 0),
    `&filter[1][field]=startAt&filter[1][operator]=$lt&filter[1][value]=${routeHistoryRange.to}`,
    `&filter[2][field]=startAt&filter[2][operator]=$gt&filter[2][value]=${
      new Date(new Date(routeHistoryRange.from).getTime() - ROUTE_HISTORY_TRIPS_LOOKBEHIND_MS).toISOString()
    }`,
  ].join('') : '';
  const { data: routeHistoryTrips, isFetching: routeHistoryTripsFetching } = useGetTripsRange(
    fleetId,
    routeHistoryTripsQuery,
    routeHistoryEnabled && !!entityType,
  );
  // Both feed the drawn path, so the overlay stays up until the legs are final.
  const routeHistoryLoading = routeHistoryEnabled && (routeHistoryFetching || routeHistoryTripsFetching);

  const routeHistory = useMemo(
    () => (routeHistoryEnabled
      ? buildRouteHistoryPath(routeHistoryData, routeHistoryTrips?.results)
      : EMPTY_ROUTE_HISTORY_PATH),
    [routeHistoryEnabled, routeHistoryData, routeHistoryTrips?.results],
  );

  // While the path is on, the period's alert markers would bury it, so only the alert the
  // user points at (or pinned) in the timeline is drawn.
  const { hovered: hoveredAlert, pinned: pinnedAlert } = useAlertFocus();
  const focusedAlerts = useMemo((): FocusedAlert[] => {
    if (!routeHistoryEnabled) return [];
    return [pinnedAlert, hoveredAlert?._id === pinnedAlert?._id ? null : hoveredAlert]
      .filter((alert): alert is FocusedAlert => !!alert && !!alert.lat && !!alert.lon);
  }, [routeHistoryEnabled, hoveredAlert, pinnedAlert]);

  // Timeline rows can only take over the map's alert markers while this map is drawing a
  // route history for a user allowed to see one.
  useEffect(() => {
    alertFocusStore.setEnabled(routeHistoryEnabled);
    return (): void => alertFocusStore.setEnabled(false);
  }, [routeHistoryEnabled]);

  const { data: geofencesList } = useGetGeofencesList(fleetId);
  const [detailsData, setDetailsData] = useState<any[]>();
  const [bounds, setBounds] = useState<GeoJSON.BBox>([0, 0, 0, 0]);
  const [clusters, setClusters] = useState<ClusterFeature<any>[]>();
  const [roadMatching, setRoadMatching] = useState<boolean>(!isOngoingTrip);
  const [autoFollowing, setIsAutoFollowing] = useState<boolean>(!!entityId);

  useEffect(() => {
    if (entityId) setIsAutoFollowing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Auto-follow keeps the camera on the current position, which fights with a route that
  // spans days - so following pauses while the history is shown and resumes when it is hidden.
  useEffect(() => {
    if (!routeHistorySupported) return;
    setIsAutoFollowing(!routeHistoryEnabled && !!entityId);
  }, [routeHistorySupported, routeHistoryEnabled, entityId]);
  const [showFakePoints, setShowFakePoints] = useState<boolean>(!isOngoingTrip);
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<Map | undefined | null>();
  const buttonRef = useRef<any>(null);
  const fakePointsRef = useRef<any>(null);
  const geofenceButtonRef = useRef<any>(null);
  const autoFollowRef = useRef<any>(null);
  // The trip details map shows one historical route, so tinting its marker with the
  // vehicle's *current* driving status would describe a different moment than the route
  // does. Keep that marker the default grey.
  const isTripDetailsMap = isTrip && entityType === 'trip';
  const markerColorById = useMarkerColors(fleetId, entityType, !isTripDetailsMap);
  const markerColor = (id?: string): string => markerColorById[id ?? ''] ?? DEFAULT_MARKER_COLOR;
  const vehicleLocations = useMemo(
    () => ((data?.length <= DETAILS_LIMIT && detailsData?.length) ? detailsData : parseLocations(data, !!entityId)) || [],
    [data, detailsData],
  );
  const alertsData = useMemo(() => (alerts.filter((alert) => alert?.lat && alert?.lon)), [alerts]);
  const [snappedVehicles, snappedAlerts, snappedVehiclesOrigin] = useSnapToRoad(
    data,
    vehicleLocations?.[0],
    alertsData,
    isTrip,
    roadMatching,
    alertsLoaded,
  );
  const [originalOngoingTripPoints, setOriginalOngoingTripPoints] = useState([]);

  useEffect(() => {
    if (isOngoingTrip) {
      const points = data?.map((p: any) => (
        [p.location.coordinates[1], p.location.coordinates[0]]));
      setOriginalOngoingTripPoints(points);
    } else {
      setOriginalOngoingTripPoints([]);
    }
  }, [data]);

  const vehiclesIDs = useMemo((): string[] => {
    if (!entityId) {
      return data?.filter((v: LocationsItem) => (v?.coordinates?.[0]?.latitude && v?.coordinates?.[0]?.longitude))?.map((v: LocationsItem) => (v.id)) ?? [];
    }
    if (isTrip) {
      const fromPoint = (data?.[0] as LocationDetails | undefined)?.vehicleId;
      if (fromPoint) {
        return [fromPoint];
      }
      if (tripVehicleId) {
        return [tripVehicleId];
      }
    }
    return [entityId];
  }, [data, entityId, isTrip, tripVehicleId]);

  const speedById = useMemo(() => {
    const base = buildSpeedMsByEntityId(data);
    if (entityId) return base;
    const et = entityType || '';
    const zeroUnlessOngoing = (ids: string[] | undefined): Record<string, number> => {
      if (ids === undefined) return base;
      const allowed = new Set(ids);
      const out = { ...base };
      Object.keys(out).forEach((k) => {
        if (!allowed.has(k)) out[k] = 0;
      });
      return out;
    };
    const applyMergedTripSpeed = (
      z: Record<string, number>,
      merged: OngoingTripSpeedDisplayMap | undefined,
    ): Record<string, number> => {
      if (!merged) return z;
      const out = { ...z };
      Object.entries(merged).forEach(([id, val]) => {
        if (typeof val === 'number') {
          out[id] = val;
        } else {
          delete out[id];
        }
      });
      return out;
    };
    if (['vehicle', 'trip', 'video'].includes(et)) {
      const z = zeroUnlessOngoing(fleetOngoingVehicleIds);
      return applyMergedTripSpeed(z, ongoingTripSpeedDisplayByVehicleId);
    }
    if (et === 'device') {
      const z = zeroUnlessOngoing(fleetOngoingDeviceIds);
      return applyMergedTripSpeed(z, ongoingTripSpeedDisplayByDeviceId);
    }
    return base;
  }, [data, entityId, entityType, fleetOngoingVehicleIds, fleetOngoingDeviceIds, ongoingTripSpeedDisplayByVehicleId, ongoingTripSpeedDisplayByDeviceId]);
  const ongoingTripPopupSpeedMs = useMemo((): number | undefined => {
    if (!isTrip || !isOngoingTrip) return undefined;
    if (tripSpeedMs === null || tripSpeedMs === undefined) return undefined;
    return tripSpeedMs;
  }, [isTrip, isOngoingTrip, tripSpeedMs]);

  const tripClusterSpeedOverlay = useMemo((): number | undefined => {
    if (!isTrip) return undefined;
    if (!isOngoingTrip) return 0;
    if (tripSpeedMs === null || tripSpeedMs === undefined) return undefined;
    return tripSpeedMs;
  }, [isTrip, isOngoingTrip, tripSpeedMs]);

  const detailMapPopupSpeedMs = useMemo((): number | undefined => {
    if (isTrip || !entityId || !Array.isArray(data) || !data.length) return undefined;
    const row = data[0] as { location?: { coordinates?: number[] } };
    if (row?.location?.coordinates == null) return undefined;
    if (hasActiveTripForSpeed === false) return 0;
    return undefined;
  }, [isTrip, entityId, data, hasActiveTripForSpeed]);

  const alertLocations = (!isTrip && !routeHistoryEnabled) ? getAlertPositions(alerts) : [];
  const heatMapLocations = getHeatmapPositions(heatMap) || [];
  const routeHistoryLocations = useMemo(
    () => routeHistory.segments.map((segment) => segment.path),
    [routeHistory],
  );
  // merge all positions
  const allLocations = useMemo(
    () => vehicleLocations.concat(alertLocations).concat(heatMapLocations).concat(routeHistoryLocations),
    [alerts, heatMap, data, routeHistoryLocations],
  );
  // used for auto-follow: only vehicle position, alerts must not pull the camera
  const vehicleOnlyLocations = useMemo(
    () => vehicleLocations.concat(heatMapLocations),
    [data, heatMap],
  );
  // snappedVehiclesOrigin already carries each point's original timestamp/speed
  // paired with its snapped location, so no index-based re-pairing with `data`.
  const newRoutePoints = snappedVehiclesOrigin
    ?.filter((value: any): any => showFakePoints || value?.isFake === false);

  const visiblePoints = useMemo(() => {
    if (!isTrip || isOngoingTrip) return [];
    return newRoutePoints;
  }, [isTrip, isOngoingTrip, showFakePoints, newRoutePoints, data]);
  const activePoints = useMemo(() => {
    if (!isTrip || isOngoingTrip) return [];

    return showFakePoints
      ? newRoutePoints
      : data?.filter((p: any) => p?.isFake === false) || [];
  }, [isTrip, isOngoingTrip, showFakePoints, newRoutePoints, data]);

  const routePath = useMemo(() => {
    if (!isTrip || !activePoints?.length) return [];

    const hasFakePoints = activePoints.some((p: any) => p?.isFake === true);

    if (!roadMatching) {
      return activePoints?.map(
        (p: any) => [p?.location?.coordinates[1], p?.location.coordinates[0]],
      );
    }

    // if (!hasFakePoints) {
    //   return activePoints?.map(
    //     (p: any) => [p?.location?.coordinates[1], p?.location?.coordinates[0]],
    //   );
    // }
    return snappedVehicles;
  }, [
    isTrip,
    roadMatching,
    activePoints,
    snappedVehicles,
  ]);
  const mapParams = useMemo(() => (getDefaultCenterAndZoom(allLocations)), [allLocations]);
  const [zoom, setZoom] = useState<number>(options.minZoom);

  const zoomTo = (lat: number, lng: number, zoomLevel = 18): void => {
    mapRef.current?.setCenter({ lat, lng });
    mapRef.current?.setZoom(zoomLevel);
  };

  const { mutate: getLocationDetails } = getLocationDetailsQuery ? getLocationDetailsQuery(
    (detailsLocations: LocationDetails[]) => {
      const locations = parseLocations(detailsLocations, true);
      setDetailsData(locations);
    },
    (error: string) => {
      setDetailsData([]);
      logger.error(`Error fetching location details: ${error}`);
    },
  ) : { mutate: () => { } };

  // center for non-follow mode: includes alerts so fitBounds covers all events
  const centerMemo = useMemo(() => ({
    lat: mapParams?.center?.lat,
    lng: mapParams?.center?.lng,
  }), [mapParams?.center?.lat, mapParams?.center?.lng]);

  useEffect(() => {
    if (mapRef.current) {
      sc.load(formatDataToGeoJsonPoints(
        vehicleLocations,
        vehiclesIDs,
        speedById,
        tripClusterSpeedOverlay,
        !isTrip && entityId ? detailMapPopupSpeedMs : undefined,
        isTrip && isOngoingTrip,
      ) as PointFeature<GeoJSON.Feature<GeoJSON.Point>>[]);
      setClusters(sc.getClusters(bounds, zoom));
    }
  }, [vehicleLocations, vehiclesIDs, speedById, tripClusterSpeedOverlay, detailMapPopupSpeedMs, isTrip, isOngoingTrip, entityId, detailsData, zoom, ...bounds]);

  const prevAutoFollowingRef = useRef(false);

  // auto-follow: fitBounds on first data load or when toggled on; pan-only on subsequent position updates
  useEffect(() => {
    if (!entityId || !autoFollowing || !mapRef.current) {
      prevAutoFollowingRef.current = autoFollowing;
      return;
    }
    const hasValidLocations = vehicleOnlyLocations.some((loc) => (
      loc.some((point: number[]) => point[0] !== 0 || point[1] !== 0)
    ));
    if (!hasValidLocations) {
      mapRef.current.setCenter(INITIAL_CENTER);
      mapRef.current.setZoom(options.minZoom);
      return;
    }

    const justToggled = !prevAutoFollowingRef.current;
    const atDefaultZoom = (mapRef.current.getZoom() ?? 0) <= options.minZoom;
    prevAutoFollowingRef.current = autoFollowing;

    if (justToggled || atDefaultZoom) {
      const customZoom = vehicleOnlyLocations.length === 1 && vehicleOnlyLocations[0].length === 2 ? 16 : undefined;
      fitMapToLocations(mapRef.current, vehicleOnlyLocations, customZoom);
      return;
    }

    const latestPoint = vehicleOnlyLocations[vehicleOnlyLocations.length - 1]?.[0];
    if (!latestPoint) return;
    const [lat, lng] = latestPoint;
    const visibleBounds = mapRef.current.getBounds();
    if (visibleBounds && !visibleBounds.contains({ lat, lng })) {
      mapRef.current.panTo({ lat, lng });
    }
  }, [autoFollowing, vehicleOnlyLocations, mapRef?.current]);

  // non-follow mode: fit to all locations (vehicle + alerts) when filter/data changes
  useEffect(() => {
    if (!mapRef.current) return;
    if (autoFollowing) return;
    const hasValidLocations = allLocations.some((loc) => (
      loc.some((point: number[]) => point[0] !== 0 || point[1] !== 0)
    ));
    if (!hasValidLocations) {
      mapRef.current.setCenter(INITIAL_CENTER);
      mapRef.current.setZoom(2);
      return;
    }
    let customZoom;
    let async = false;
    if (allLocations.length === 1) {
      if (allLocations[0].length === 2) {
        customZoom = 16;
      }
      if (alertLocations.length === 1) {
        customZoom = 9;
        async = true;
      }
    }
    fitMapToLocations(mapRef.current, allLocations, customZoom, async);
  }, [autoFollowing, mapRef?.current]);

  // Frame the whole route once per new history result, so switching the period (or turning
  // the overlay on) always brings the path into view without hijacking later map panning.
  const routeHistoryFitKeyRef = useRef<string>('');
  useEffect(() => {
    if (!mapRef.current || !routeHistoryLocations.length) return;
    const fitKey = `${routeHistoryRange?.from}|${routeHistoryRange?.to}|${routeHistory.pointCount}`;
    if (routeHistoryFitKeyRef.current === fitKey) return;
    routeHistoryFitKeyRef.current = fitKey;
    fitMapToLocations(mapRef.current, routeHistoryLocations);
  }, [routeHistoryLocations, routeHistoryRange, routeHistory.pointCount, googleMap]);

  // A pinned alert is a deliberate "show me this one" - bring it into view. Hovering must
  // not move the camera, or scanning down the list would make the map jump around.
  useEffect(() => {
    if (!pinnedAlert?.lat || !pinnedAlert?.lon || !mapRef.current) return;
    mapRef.current.panTo({ lat: pinnedAlert.lat, lng: pinnedAlert.lon });
  }, [pinnedAlert, googleMap]);

  useEffect(() => {
    if (buttonRef.current && mapRef.current) {
      if (roadMatching) { buttonRef.current.classList.add('active'); }
      if (!roadMatching) { buttonRef.current.classList.remove('active'); }
    }
  }, [roadMatching, mapRef.current]);
  useEffect(() => {
    if (fakePointsRef.current && mapRef.current) {
      if (showFakePoints) { fakePointsRef.current.classList.add('active'); }
      if (!showFakePoints) { fakePointsRef.current.classList.remove('active'); }
    }
  }, [showFakePoints, mapRef.current]);

  useEffect(() => {
    if (FeaturesManagement.features.geofence && geofenceButtonRef.current && mapRef.current) {
      if (showGeofences) { geofenceButtonRef.current.classList.add('active'); }
      if (!showGeofences) { geofenceButtonRef.current.classList.remove('active'); }
    }
  }, [showGeofences, mapRef.current]);

  useEffect(() => {
    if (autoFollowRef.current && mapRef.current) {
      if (autoFollowing && entityId) { autoFollowRef.current.classList.add('active'); }
      if (!autoFollowing || !entityId) { autoFollowRef.current.classList.remove('active'); }
    }
  }, [autoFollowing, entityId, mapRef.current]);

  useEffect(() => {
    if (mapRef.current && !isTrip && isSuccess && usePolygons) {
      const bnds = mapRef.current.getBounds()?.toJSON();
      if (bnds) {
        setFilters((props) => ({
          ...props,
          polygons: [
            [bnds.east, bnds.north],
            [bnds.east, bnds.south],
            [bnds.west, bnds.south],
            [bnds.west, bnds.north],
            [bnds.east, bnds.north],
          ],
        }));
      }
    }
  }, [...bounds]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
    libraries: libs as any,
  });

  useAlertsHeatmapDeckGl(googleMap, heatMap);

  useEffect(() => {
    if (!isLoaded) {
      return undefined;
    }
    const syncMap = (): void => {
      if (mapRef.current) {
        setGoogleMap(mapRef.current);
      }
    };
    syncMap();
    const timerId = window.setTimeout(syncMap, 0);
    return () => window.clearTimeout(timerId);
  }, [isLoaded]);

  useEffect(() => {
    if (!isOngoingTrip) {
      setRoadMatching(true);
    }
  }, [isOngoingTrip]);

  const handleZoomChanged = (): void => {
    if (mapRef.current) {
      setZoom(mapRef.current?.zoom);
    }
  };

  const handleBoundsChanged = useCallback((): void => {
    if (mapRef.current) {
      const bnds = mapRef.current.getBounds()?.toJSON();
      setBounds([bnds?.west || 0, bnds?.south || 0, bnds?.east || 0, bnds?.north || 0]);
    }
  }, [...bounds]);

  const toggleRoadMatching = (): void => {
    setRoadMatching((state) => !state);
  };

  const toggleFakePoints = (): void => {
    setShowFakePoints((state) => !state);
  };

  const toggleAutoFollow = (): void => {
    if (!entityId) return;
    setIsAutoFollowing((state) => !state);
  };

  const toggleShowGeofences = (): void => {
    setShowGeofences((state) => {
      setShowGeofencesStorage(!state);
      return !state;
    });
  };

  const toggleRouteHistory = (): void => {
    setShowRouteHistory((state) => {
      setShowRouteHistoryStorage(!state);
      return !state;
    });
  };

  const routeHistoryButtonRef = useRef<HTMLElement | null>(null);

  // Added from an effect rather than from onLoad: on detail pages the entity id often
  // arrives after the map has already loaded.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeHistorySupported) return undefined;
    const div = document.createElement('div');
    const customButton = createCustomButton({
      title: 'Show route history',
      className: 'route-history',
      onClick: toggleRouteHistory,
    });
    routeHistoryButtonRef.current = customButton;
    div.appendChild(customButton);
    const controls = map.controls[google.maps.ControlPosition.TOP_LEFT];
    controls.push(div);

    return (): void => {
      const index = controls.getArray().indexOf(div);
      if (index >= 0) controls.removeAt(index);
      routeHistoryButtonRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMap, routeHistorySupported]);

  useEffect(() => {
    const button = routeHistoryButtonRef.current;
    if (!button) return;
    button.classList.toggle('active', routeHistoryEnabled);
    button.title = getRouteHistoryTitle(routeHistoryRange, showRouteHistory);
  }, [routeHistoryEnabled, routeHistoryRange, showRouteHistory, googleMap]);

  const handleMapLoad = useCallback((map: google.maps.Map): void => {
    mapRef.current = map as Map;
    setGoogleMap(map);
    if (isTrip && !isOngoingTrip && !isFleetUser) {
      const div = document.createElement('div');
      const customButton = createCustomButton({
        title: 'Snap To Road',
        className: 'snap-to-road',
        onClick: toggleRoadMatching,
      });
      buttonRef.current = customButton;
      div.appendChild(customButton);
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(div);
    }
    if (isTrip && !isOngoingTrip && !isFleetUser) {
      const div = document.createElement('div');
      const customButton = createCustomButton({
        title: 'Interpolate',
        className: 'fake-points',
        onClick: toggleFakePoints,
      });
      fakePointsRef.current = customButton;
      div.appendChild(customButton);
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(div);
    }

    // add geofence control element
    if (FeaturesManagement.features.geofence) {
      const div = document.createElement('div');
      const customButton = createCustomButton({
        title: 'Show geofences',
        className: 'show-geofences',
        onClick: toggleShowGeofences,
      });
      geofenceButtonRef.current = customButton;
      div.appendChild(customButton);
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(div);
    }

    if (entityId) {
      const div = document.createElement('div');
      const customButton = createCustomButton({
        title: 'Auto follow',
        className: 'autofollow',
        onClick: toggleAutoFollow,
      });
      autoFollowRef.current = customButton;
      div.appendChild(customButton);
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(div);
    }
  }, [mapRef, entityId]);

  if (!isLoaded) {
    return <MapWidgetSkeleton />;
  }

  function handleClusterClick({ id, lat, lng }: { id: number, lat: number, lng: number }): void {
    const expansionZoom = Math.min(sc.getClusterExpansionZoom(id), options.maxZoom);
    mapRef.current?.setZoom(expansionZoom);
    mapRef.current?.panTo({ lat, lng });
  }
  return (
    <S.Wrapper className="map-wrapper" style={wrapperStyle}>
      <GoogleMap
        mapContainerClassName="map-container"
        mapContainerStyle={containerStyle}
        center={INITIAL_CENTER}
        zoom={options.minZoom}
        options={mapOptions}
        onLoad={handleMapLoad as VoidFunction}
        onUnmount={() => {
          mapRef.current = null;
          setGoogleMap(null);
        }}
        onBoundsChanged={handleBoundsChanged as VoidFunction}
        onZoomChanged={handleZoomChanged as VoidFunction}
        onIdle={() => {
          if (mapRef.current) {
            setGoogleMap(mapRef.current);
          }
        }}
        onDrag={() => setIsAutoFollowing(false)}
      >
        {!isTrip && (clusters?.map(({ id, geometry, properties }, idx: number) => {
          const [lng, lat] = geometry.coordinates;
          const {
            cluster, point_count: pointCount, location, id: vehicleId, speedMs,
          } = properties;

          return cluster ? (
            <MarkerF
              key={createKey([[lat, lng]], idx)}
              onClick={() => handleClusterClick({ id: id as number, lat, lng })}
              position={{ lat, lng }}
              icon={
                { url: ClusterPng }
              }
              label={getLabel(pointCount, theme)}
            />
          )
            : !!location && (
              <VehicleMarker
                key={`vehicle-${vehicleId ?? entityId ?? idx}`}
                path={location}
                fleetId={fleetId}
                entityId={vehicleId ?? entityId}
                entityType={entityType}
                speedMs={speedMs}
                zoom={mapRef.current?.zoom}
                color={markerColor(vehicleId ?? entityId)}
                showSpeedInVehicleInfo={showSpeedInVehicleInfo}
                showDetailsButton={showDetailsButton}
              />
            );
        }))}
        {isTrip && !isOngoingTrip && snappedVehicles?.length && (
          <VehicleRoute
            path={routePath}
            roadMatching={roadMatching}
            hasTripVehicleMarker={hasTripVehicleMarker}
          />
        )}
        {isTrip && !isOngoingTrip && snappedVehicles?.length && clusters?.[0] && (
          <VehicleMarker
            key="vehicle-position"
            path={clusters?.[0].properties.location}
            fleetId={fleetId}
            entityId={clusters?.[0].properties.id ?? tripVehicleId}
            entityType={entityType}
            speedMs={0}
            zoom={mapRef.current?.zoom}
            color={markerColor(clusters?.[0].properties.id ?? tripVehicleId)}
            showSpeedInVehicleInfo={showSpeedInVehicleInfo}
            showDetailsButton={showDetailsButton}
          />
        )}
        {isTrip && isOngoingTrip && originalOngoingTripPoints?.length && (
          <VehicleRoute
            path={originalOngoingTripPoints}
            roadMatching={false}
            hasTripVehicleMarker={hasTripVehicleMarker}
          />
        )}
        {isTrip && isOngoingTrip && originalOngoingTripPoints?.length && (
          <VehicleMarker
            key="vehicle-position"
            path={originalOngoingTripPoints}
            fleetId={fleetId}
            entityId={clusters?.[0]?.properties.id ?? tripVehicleId}
            entityType={entityType}
            speedMs={ongoingTripPopupSpeedMs}
            zoom={mapRef.current?.zoom}
            color={markerColor(clusters?.[0]?.properties.id ?? tripVehicleId)}
            noAnimation
            showSpeedInVehicleInfo={showSpeedInVehicleInfo}
            showDetailsButton={showDetailsButton}
          />
        )}
        {routeHistoryEnabled && (
          <RouteHistory
            segments={routeHistory.segments}
            stops={routeHistory.stops}
            start={routeHistory.start}
            end={routeHistory.end}
          />
        )}
        {!routeHistoryEnabled && (isTrip ? snappedAlerts : alertsData)?.map((alert) => (
          <AlertMarker
            key={alert._id}
            id={alert._id}
            lat={alert.lat as number}
            lng={alert.lon as number}
            name={alert.name}
            riskLevel={alert.riskLevel}
            disableNavigation={disableAlertMarkerNavigation}
          />
        ))}
        {focusedAlerts.map((alert) => (
          <AlertMarker
            key={`focused-${alert._id}`}
            id={alert._id}
            lat={alert.lat}
            lng={alert.lon}
            name={alert.name}
            riskLevel={alert.riskLevel}
            disableNavigation={disableAlertMarkerNavigation}
          />
        ))}
        {FeaturesManagement.map.routePoints && isTrip && !isOngoingTrip && visiblePoints?.map((gpsPoint: any) => (
          <PointMarker
            key={gpsPoint?._id}
            lat={gpsPoint?.location?.coordinates[1]}
            lng={gpsPoint?.location?.coordinates[0]}
            speed={gpsPoint?.speedMs}
            timestampSecEpoch={gpsPoint?.timestampSecEpoch}
          />
        ))}
        {isTrip && !isOngoingTrip && (
          <button
            ref={buttonRef}
            type="button"
            aria-label="Road matching"
            className="snap-to-road"
          />
        )}
        {FeaturesManagement.features.geofence && showGeofences && (geofencesList?.results?.map((g: Geofence) => {
          const geofence = g.location?.coordinates?.[0];

          if (isCirclePolygon(g)) {
            const { center, radius } = getCircleParams(g);
            return (
              <CircleF
                key={`circle-${g._id}`}
                center={center}
                radius={radius}
                options={GEOFENCE_STYLE}
              />
            );
          }

          const path = geofence?.map((p: any) => ({ lat: p[1], lng: p[0] }));
          return (
            <PolygonF
              path={path}
              key={`polygon-${g._id}`}
              options={GEOFENCE_STYLE}
            />
          );
        }))}
        {FeaturesManagement.features.geofence && (
          <button
            ref={geofenceButtonRef}
            type="button"
            aria-label="Show geofences"
            className="show-geofences"
          />
        )}
      </GoogleMap>
      {routeHistoryLoading && (
        <S.RouteHistoryLoader>
          <div className="loader-card">
            <span className="spinner" />
            <span>Loading path...</span>
          </div>
        </S.RouteHistoryLoader>
      )}
    </S.Wrapper>
  );
}

export default MapWidget;
